/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Lightmetrics Pooler API
 * This API handles the listing of two kind of videos: events and footages. Video events are automatically triggered by the dashcam on the vehicle whenever an event of interest happens.
 * Video footages can be requested given an arbitrary timestamp and duration of a valid vehicle trip. It can take significant time for the request to complete, since the dashcam needs to be online to upload the requested footage.
 * OpenAPI spec version: v1.0.0
 */
import * as zod from 'zod/v4';

export const UploadCoachingFileRequest = zod.object({
  user_id: zod.uuid(),
  fleet_id: zod.uuid(),
  type: zod.enum(['VIDEO_FRAME', 'CUSTOM_UPLOAD', 'THUMBNAIL']),
  file_timestamp: zod.int().nullish(),
  file: zod.instanceof(Blob),
});

export type UploadCoachingFileRequest = zod.input<typeof UploadCoachingFileRequest>;
export type UploadCoachingFileRequestOutput = zod.output<typeof UploadCoachingFileRequest>;

export const UploadCoachingFileDTO = zod.object({
  message: zod.string(),
  code: zod.enum(['OK', 'NO_FILE', 'FILE_TOO_LARGE', 'WRONG_CONTENT_TYPE', 'EVIDENCE_NOT_FOUND', 'COACHING_ALREADY_FINISHED', 'MAX_FILES_REACHED']),
  coaching_file_id: zod.uuid().nullish(),
  coaching_file_url: zod.string().nullish(),
});

export type UploadCoachingFileDTO = zod.input<typeof UploadCoachingFileDTO>;
export type UploadCoachingFileDTOOutput = zod.output<typeof UploadCoachingFileDTO>;

export const CoachingAlreadyCompleted = UploadCoachingFileDTO;

export type CoachingAlreadyCompleted = zod.input<typeof CoachingAlreadyCompleted>;
export type CoachingAlreadyCompletedOutput = zod.output<typeof CoachingAlreadyCompleted>;

export const EvidenceNotFound = UploadCoachingFileDTO;

export type EvidenceNotFound = zod.input<typeof EvidenceNotFound>;
export type EvidenceNotFoundOutput = zod.output<typeof EvidenceNotFound>;

export const FileTooLarge = UploadCoachingFileDTO;

export type FileTooLarge = zod.input<typeof FileTooLarge>;
export type FileTooLargeOutput = zod.output<typeof FileTooLarge>;

export const MaxFilesReached = UploadCoachingFileDTO;

export type MaxFilesReached = zod.input<typeof MaxFilesReached>;
export type MaxFilesReachedOutput = zod.output<typeof MaxFilesReached>;

export const NoFile = UploadCoachingFileDTO;

export type NoFile = zod.input<typeof NoFile>;
export type NoFileOutput = zod.output<typeof NoFile>;

export const Success = UploadCoachingFileDTO;

export type Success = zod.input<typeof Success>;
export type SuccessOutput = zod.output<typeof Success>;

export const WrongContentType = UploadCoachingFileDTO;

export type WrongContentType = zod.input<typeof WrongContentType>;
export type WrongContentTypeOutput = zod.output<typeof WrongContentType>;

export const ListCoachingFeedbackRequest = zod.object({
  user_id: zod.uuid(),
  fleet_id: zod.uuid(),
  groups_restriction: zod.array(zod.string()),
  driver_ids: zod.array(zod.uuid()).nullish(),
  media_id: zod.uuid().nullish(),
  from_updated_at: zod.iso.datetime({ offset: true }).nullish(),
  to_updated_at: zod.iso.datetime({ offset: true }).nullish(),
  safety_occurrence_ids: zod.array(zod.uuid()).nullish(),
  status: zod.array(zod.enum(['ADDING_EVIDENCES', 'APPLYING', 'FINISHED'])).nullish(),
});

export type ListCoachingFeedbackRequest = zod.input<typeof ListCoachingFeedbackRequest>;
export type ListCoachingFeedbackRequestOutput = zod.output<typeof ListCoachingFeedbackRequest>;

export const SafetyOccurrence = zod
  .object({
    id: zod.uuid().describe('Safety occurrence ID'),
    name: zod.string().describe('Occurrence label name'),
  })
  .describe('Type of registered occurrence');

export type SafetyOccurrence = zod.input<typeof SafetyOccurrence>;
export type SafetyOccurrenceOutput = zod.output<typeof SafetyOccurrence>;

export const MediaDetails = zod.looseObject({}).describe('Details for the given media type.');

export type MediaDetails = zod.input<typeof MediaDetails>;
export type MediaDetailsOutput = zod.output<typeof MediaDetails>;

export const Event = MediaDetails.and(
  zod.object({
    media_url: zod.string().nullable().describe('The url of the content media.'),
    thumbnail_url: zod.string().nullable().describe('The url to the thumbnail image of a video.'),
    event_trigger: zod
      .union([
        zod.literal('TRAFFIC_SPEED_VIOLATED'),
        zod.literal('HARSH_BREAKING'),
        zod.literal('HARSH_ACCELERATION'),
        zod.literal('HARSH_CORNERING'),
        zod.literal('COLLISION'),
        zod.literal('STOP_SIGN_VIOLATED'),
        zod.literal('LANE_DRIFT'),
        zod.literal('TAILGATING'),
        zod.literal('ANOMALY'),
        zod.literal('DISTRACTED_DRIVING'),
        zod.literal('UNKNOWN'),
        zod.literal('YAWN'),
        zod.literal('EYES_CLOSED'),
        zod.literal('PHONE_USAGE'),
        zod.literal('SMOKING'),
        zod.literal('FRONT_COLLISION_WARNING'),
        zod.literal('FATIGUE'),
        zod.literal('MAX_SPEED_EXCEEDED'),
        zod.literal('SOS'),
        zod.literal('PEDESTRIAN_COLLISION'),
        zod.literal('NOT_WEARING_SEAT_BELTS'),
        zod.literal('IDLE_SPEED'),
        zod.literal('LOW_VOLTAGE'),
        zod.literal('ILLEGAL_SHUTDOWN'),
        zod.literal('TEMPERATURE_ABNORMAL'),
        zod.literal('INSUFFICIENT_ELECTRICITY_ALARM'),
        zod.literal('VIDEO_BLOCK'),
        zod.literal('SPEED_LIMIT_SIGN'),
        zod.literal('PASSENGER_NOT_WEARING_SEAT_BELTS'),
        zod.literal('SERIOUS_THEFT'),
        zod.literal('ANTI_DESTRUCTION'),
        zod.literal('EATING'),
        zod.literal('OVERCROWDING'),
        zod.literal('G_SENSOR_WAKEUP'),
        zod.literal('MISSINGFACE'),
        zod.literal(null),
      ])
      .nullable()
      .describe('Event trigger.'),
    latitude: zod.number().nullish().describe('Event latitude.'),
    longitude: zod.number().nullish().describe('Event longitude.'),
    speed: zod.number().nullable().describe('Event speed.'),
    speed_sign: zod.int().nullish().describe('Speed limit of the road. Only available when event trigger is TRAFFICSPEEDVIOLATED.'),
  })
);

export type Event = zod.input<typeof Event>;
export type EventOutput = zod.output<typeof Event>;

export const Footage = MediaDetails.and(
  zod.object({
    duration: zod.int().nullable().describe('Media duration in seconds'),
    media_url: zod.string().nullish().describe('The url of the content media. Only available if the media has been uploaded.'),
    thumbnail_url: zod.string().nullish().describe('The url to the thumbnail image of a video. Only available if the media has been uploaded.'),
    start_latitude: zod.number().nullish().describe('Footage start latitude. Only available if the media has been uploaded.'),
    start_longitude: zod.number().nullish().describe('Footage start longitude. Only available if the media has been uploaded.'),
    end_latitude: zod.number().nullish().describe('Footage end latitude. Only available if the media has been uploaded.'),
    end_longitude: zod.number().nullish().describe('Footage end longitude. Only available if the media has been uploaded.'),
    request_id: zod.string().nullish().describe('Request id of the footage.'),
    created_at: zod.int().nullable().describe('Unix timestamp in millis of request creation.'),
    updated_at: zod.int().nullable().describe('Unix timestamp in millis of request status update.'),
    processed_at: zod.int().nullish().describe('Time the request was processed at'),
    status: zod
      .union([
        zod.literal('PENDING'),
        zod.literal('ACKNOWLEDGE'),
        zod.literal('STARTED'),
        zod.literal('CORRUPTED'),
        zod.literal('FINISHED'),
        zod.literal('UNAVAILABLE'),
        zod.literal('FAILED'),
        zod.literal('CANCELED'),
        zod.literal(null),
      ])
      .nullable()
      .describe('Status of footage request.'),
    created_by: zod.uuid().nullish().describe('Unique identifier of the user who requested the footage.'),
    trigger_id: zod.uuid().nullish().describe('Unique identifier of the trigger who requested the footage.'),
    request_status: zod
      .union([
        zod.literal('PENDING_REQUEST'),
        zod.literal('AMOUNT_EXCEEDED'),
        zod.literal('PERIOD_UNAVAILABLE'),
        zod.literal('REQUESTED'),
        zod.literal('TIMEOUT_REQUEST'),
        zod.literal('FINISHED_REQUEST'),
        zod.literal('LM_ERROR_FAILED'),
        zod.literal('LM_ERROR_UNAVAILABLE'),
        zod.literal('FAILED_REQUEST'),
        zod.literal('CORRUPTED_VIDEO'),
        zod.literal('RETRY_REQUESTED'),
        zod.literal('REQUEST_ACKNOWLEDGED'),
        zod.literal('MANUAL_RETRY_REQUEST'),
        zod.literal(null),
      ])
      .nullish()
      .describe('Status for the request of the footage.'),
  })
);

export type Footage = zod.input<typeof Footage>;
export type FootageOutput = zod.output<typeof Footage>;

export const SafetyReview = zod.object({
  id: zod.uuid().describe('Safety review ID'),
  fleet_id: zod.uuid().describe('Fleet ID'),
  status: zod
    .union([zod.literal('RELEVANT'), zod.literal('DISMISSED'), zod.literal(null)])
    .nullish()
    .describe('Current safety review status'),
  driver_id: zod.uuid().nullish().describe('Driver ID associated with review'),
  original_media_driver_id: zod.uuid().nullish().describe("Media's Driver ID before review was created"),
  severity: zod
    .union([zod.literal('LOW'), zod.literal('MEDIUM'), zod.literal('HIGH'), zod.literal('HIGHER'), zod.literal(null)])
    .nullish()
    .describe('Severity associated with review'),
  original_media_severity: zod
    .union([zod.literal('LOW'), zod.literal('MEDIUM'), zod.literal('HIGH'), zod.literal('HIGHER'), zod.literal(null)])
    .nullish()
    .describe("Media's severity before review was created"),
  dismiss_reason: zod
    .union([
      zod.literal('NO_EVENT_OR_OCCURRENCE'),
      zod.literal('WRONG_EVENT_OR_OCCURRENCE'),
      zod.literal('BLURRY_OR_LOW_QUALITY'),
      zod.literal('EVENT_NOT_INTERESTING'),
      zod.literal(null),
    ])
    .nullish()
    .describe('Reason used on dismissing'),
  created_by: zod.uuid().describe('ID of the user that first reviewed'),
  updated_by: zod.uuid().describe('ID of the user that last updated'),
  created_at: zod.iso.datetime({ offset: true }).nullish().describe('Datetime of review creation'),
  updated_at: zod.iso.datetime({ offset: true }).nullish().describe('Datetime of review last update'),
  occurrences: zod.array(SafetyOccurrence).describe('Associated occurrences'),
});

export type SafetyReview = zod.input<typeof SafetyReview>;
export type SafetyReviewOutput = zod.output<typeof SafetyReview>;

export const RiskFactor = zod
  .object({
    id: zod.uuid(),
    type: zod.string(),
    value: zod.number().describe('Value of risk factor'),
    custom_details: zod.record(zod.string(), zod.looseObject({}).describe('JSON of custom type details')).nullish().describe('JSON of custom type details'),
  })
  .describe('List of risk factors identified');

export type RiskFactor = zod.input<typeof RiskFactor>;
export type RiskFactorOutput = zod.output<typeof RiskFactor>;

export const Severity = zod
  .object({
    id: zod.uuid().describe('The id of the severity'),
    severity_level: zod.enum(['LOW', 'MEDIUM', 'HIGH', 'HIGHER']).describe('The severity level'),
    severity_score: zod.int().describe('The overall computed score'),
    risk_factors: zod.array(RiskFactor).describe('List of risk factors identified'),
  })
  .describe('Event severity detailed');

export type Severity = zod.input<typeof Severity>;
export type SeverityOutput = zod.output<typeof Severity>;

export const MediaComment = zod
  .object({
    id: zod.uuid().describe('Media comment ID'),
    media_id: zod.uuid().describe('Media ID associated with comment'),
    created_by: zod.uuid().describe('ID of the user that created the comment'),
    content: zod.string().nullish().describe('Content of the comment'),
    created_at: zod.iso.datetime({ offset: true }).describe('Datetime of comment creation'),
    updated_at: zod.iso.datetime({ offset: true }).nullish().describe('Datetime of comment last update'),
  })
  .describe('Comments for the media');

export type MediaComment = zod.input<typeof MediaComment>;
export type MediaCommentOutput = zod.output<typeof MediaComment>;

export const SeatbeltDetectionDTO = zod.object({
  with_seatbelt: zod.boolean().nullish(),
});

export type SeatbeltDetectionDTO = zod.input<typeof SeatbeltDetectionDTO>;
export type SeatbeltDetectionDTOOutput = zod.output<typeof SeatbeltDetectionDTO>;

export const AiVideoTagsDTO = zod
  .object({
    seatbelt_detection: zod.union([SeatbeltDetectionDTO, zod.null()]).optional(),
    potential_cellphone_usage: zod.boolean().nullish(),
  })
  .describe('AI tags detected in the video');

export type AiVideoTagsDTO = zod.input<typeof AiVideoTagsDTO>;
export type AiVideoTagsDTOOutput = zod.output<typeof AiVideoTagsDTO>;

export const Media = zod
  .object({
    id: zod.uuid().describe('Media identifier in the UUID format'),
    device_id: zod.string().describe('Device identifier. A number between 10 and 15 digits'),
    vehicle_id: zod.uuid().nullish().describe('Vehicle identifier'),
    group_path: zod.string().nullish().describe('Group path'),
    media_details: zod.union([Event, Footage, zod.null()]).optional(),
    safety_review: zod.union([SafetyReview, zod.null()]).optional(),
    media_type: zod.enum(['FOOTAGE_VIDEO', 'EVENT_VIDEO', 'TIMELAPSE']).describe('Type of media content.'),
    driver_id: zod.uuid().nullish().describe('Driver identifier UUID. Available only if a driver was assigned to the device ID at video start timestamp.'),
    ai_suggested_driver_id: zod.uuid().nullish().describe('Driver identifier UUID. Suggested by FaceID'),
    timestamp: zod.int().describe('Video start Unix timestamp in millis.'),
    media_view_count: zod.int().describe('User views count'),
    severity: zod
      .union([zod.literal('LOW'), zod.literal('MEDIUM'), zod.literal('HIGH'), zod.literal('HIGHER'), zod.literal(null)])
      .nullish()
      .describe('Event severity.'),
    severity_detail: zod.union([Severity, zod.null()]).optional(),
    is_favorite: zod.boolean().describe('Favorite media'),
    media_status: zod.enum(['RELEVANT', 'DISMISSED', 'WATCHED', 'NEW']).describe('Status of the media'),
    ai_description: zod.string().nullish().describe('Video description'),
    suggested_occurrences: zod.array(zod.string().describe('AI Suggested occurrences')).nullish().describe('AI Suggested occurrences'),
    comments: zod.array(MediaComment).nullish().describe('Comments for the media'),
    ai_video_tags: zod.union([AiVideoTagsDTO, zod.null()]).optional(),
  })
  .describe('Evidence Media');

export type Media = zod.input<typeof Media>;
export type MediaOutput = zod.output<typeof Media>;

export const CoachingEvidenceFile = zod
  .object({
    id: zod.uuid().describe('Evidence File ID'),
    file_type: zod.enum(['VIDEO_FRAME', 'CUSTOM_UPLOAD', 'THUMBNAIL']).describe('File type'),
    file_url: zod.string().nullish().describe('Signed File URL'),
    file_timestamp: zod.int().nullish().describe('Video frame time (seconds). Applies for VIDEO_FRAME'),
    created_at: zod.iso.datetime({ offset: true }).describe('Creation timestamp'),
  })
  .describe('Evidence Media');

export type CoachingEvidenceFile = zod.input<typeof CoachingEvidenceFile>;
export type CoachingEvidenceFileOutput = zod.output<typeof CoachingEvidenceFile>;

export const CoachingEvidence = zod.object({
  id: zod.uuid().nullish().describe('Evidence ID'),
  type: zod
    .union([zod.literal('MEDIA'), zod.literal(null)])
    .nullish()
    .describe('Type of the evidence'),
  safety_occurrences: zod.array(SafetyOccurrence).nullish().describe('Evidence occurrences. Same as SafetyReview of the Media'),
  media_id: zod.uuid().describe('Evidence Media ID'),
  media: zod.union([Media, zod.null()]).optional(),
  files: zod.array(CoachingEvidenceFile).nullish().describe('Evidence Media'),
});

export type CoachingEvidence = zod.input<typeof CoachingEvidence>;
export type CoachingEvidenceOutput = zod.output<typeof CoachingEvidence>;

export const CoachingFeedback = zod.object({
  id: zod.uuid().describe('Coaching feedback ID'),
  driver_id: zod.uuid().describe('Driver associated to the coaching'),
  created_at: zod.iso.datetime({ offset: true }).describe('Coaching creation timestamp'),
  created_by: zod.uuid().describe('ID of the user that created the coaching'),
  updated_at: zod.iso.datetime({ offset: true }).nullish().describe('Coaching update timestamp'),
  updated_by: zod.uuid().nullish().describe('ID of the user that last updated'),
  status: zod.enum(['ADDING_EVIDENCES', 'APPLYING', 'FINISHED']).describe('Coaching status'),
  feedback_action: zod
    .union([zod.literal('GUIDANCE'), zod.literal('WARNING'), zod.literal('SUSPENSION'), zod.literal('DISMISSAL'), zod.literal('NO_ACTION'), zod.literal(null)])
    .nullish()
    .describe('Coaching action'),
  note: zod.string().nullish().describe('General coaching annotation'),
  note_author_id: zod.uuid().nullish().describe('ID of the user that changed the note'),
  note_created_at: zod.iso.datetime({ offset: true }).nullish().describe('Note creation timestamp'),
  coaching_evidences: zod.array(CoachingEvidence),
});

export type CoachingFeedback = zod.input<typeof CoachingFeedback>;
export type CoachingFeedbackOutput = zod.output<typeof CoachingFeedback>;

export const PageCoachingFeedback = zod.object({
  total_elements: zod.int().nullish(),
  total_pages: zod.int().nullish(),
  size: zod.int().nullish(),
  content: zod.array(CoachingFeedback).nullish(),
  number: zod.int().nullish(),
  first: zod.boolean().nullish(),
  last: zod.boolean().nullish(),
  number_of_elements: zod.int().nullish(),
  empty: zod.boolean().nullish(),
});

export type PageCoachingFeedback = zod.input<typeof PageCoachingFeedback>;
export type PageCoachingFeedbackOutput = zod.output<typeof PageCoachingFeedback>;

export const CreateCoachingFeedbackRequest = zod.object({
  user_id: zod.uuid(),
  fleet_id: zod.uuid(),
  media_ids: zod.array(zod.uuid()),
  driver_id: zod.uuid(),
});

export type CreateCoachingFeedbackRequest = zod.input<typeof CreateCoachingFeedbackRequest>;
export type CreateCoachingFeedbackRequestOutput = zod.output<typeof CreateCoachingFeedbackRequest>;

export const UpdateCoachingFeedbackRequest = zod.object({
  user_id: zod.uuid(),
  fleet_id: zod.uuid(),
  media_ids: zod.array(zod.uuid()).nullish(),
  status: zod.union([zod.literal('ADDING_EVIDENCES'), zod.literal('APPLYING'), zod.literal('FINISHED'), zod.literal(null)]).nullish(),
  note: zod.string().nullish(),
  feedback_action: zod
    .union([zod.literal('GUIDANCE'), zod.literal('WARNING'), zod.literal('SUSPENSION'), zod.literal('DISMISSAL'), zod.literal('NO_ACTION'), zod.literal(null)])
    .nullish(),
});

export type UpdateCoachingFeedbackRequest = zod.input<typeof UpdateCoachingFeedbackRequest>;
export type UpdateCoachingFeedbackRequestOutput = zod.output<typeof UpdateCoachingFeedbackRequest>;
/**
 * @summary Uploads a file related to a coaching evidence
 */
export const UploadFileParams = zod.object({
  coachingEvidenceId: zod.uuid(),
});

export const UploadFileBody = UploadCoachingFileRequest;

export const UploadFileResponse = zod.union([CoachingAlreadyCompleted, EvidenceNotFound, FileTooLarge, MaxFilesReached, NoFile, Success, WrongContentType]);

/**
 * @summary List Coaching Feedbacks
 */
export const listCoachingFeedbacksQueryPageDefault = 0;
export const listCoachingFeedbacksQueryPageMin = 0;

export const listCoachingFeedbacksQuerySizeDefault = 10;

export const listCoachingFeedbacksQuerySortDefault = ['updatedAt,DESC'];

export const ListCoachingFeedbacksQueryParams = zod.object({
  request: zod.object({
    user_id: zod.uuid(),
    fleet_id: zod.uuid(),
    groups_restriction: zod.array(zod.string()),
    driver_ids: zod.array(zod.uuid()).nullish(),
    media_id: zod.uuid().nullish(),
    from_updated_at: zod.iso.datetime({ offset: true }).nullish(),
    to_updated_at: zod.iso.datetime({ offset: true }).nullish(),
    safety_occurrence_ids: zod.array(zod.uuid()).nullish(),
    status: zod.array(zod.enum(['ADDING_EVIDENCES', 'APPLYING', 'FINISHED'])).nullish(),
  }),
  page: zod.int().min(listCoachingFeedbacksQueryPageMin).default(listCoachingFeedbacksQueryPageDefault).describe('Zero-based page index (0..N)'),
  size: zod.int().min(1).default(listCoachingFeedbacksQuerySizeDefault).describe('The size of the page to be returned'),
  sort: zod
    .array(zod.string())
    .default(listCoachingFeedbacksQuerySortDefault)
    .describe('Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.'),
});

export const ListCoachingFeedbacksResponse = PageCoachingFeedback;

/**
 * @summary Creates a new Coaching Feedback
 */
export const CreateCoachingFeedbackBody = CreateCoachingFeedbackRequest;

export const CreateCoachingFeedbackResponse = CoachingFeedback;

/**
 * @summary Gets a Coaching Feedback by ID
 */
export const GetCoachingFeedbackParams = zod.object({
  coachingFeedbackId: zod.uuid(),
});

export const GetCoachingFeedbackResponse = CoachingFeedback;

/**
 * @summary Delete Coaching Feedback
 */
export const DeleteCoachingFeedbackParams = zod.object({
  coachingFeedbackId: zod.uuid(),
});

export const DeleteCoachingFeedbackResponse = zod.unknown();

/**
 * @summary Updates a Coaching Feedback
 */
export const UpdateCoachingFeedbackParams = zod.object({
  coachingFeedbackId: zod.uuid(),
});

export const UpdateCoachingFeedbackBody = UpdateCoachingFeedbackRequest;

export const UpdateCoachingFeedbackResponse = CoachingFeedback;

/**
 * @summary Delete Coaching File
 */
export const DeleteCoachingFileParams = zod.object({
  coachingFileId: zod.uuid(),
  coachingEvidenceId: zod.uuid(),
});

export const DeleteCoachingFileResponse = zod.unknown();
